import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { cities } from './cities';
import './CitySelectionModal.css';

const CitySelectionModal = ({ isOpen, onClose, onCitySelect }) => {
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCities, setFilteredCities] = useState(cities);
  const searchInputRef = useRef(null);
  const [isMobilePhone, setIsMobilePhone] = useState(false);

  useEffect(() => {
    // Basic detection to differentiate phones from tablets/desktop
    // We rely primarily on "Mobile" in the UA string which is present on phones
    // but typically not on tablets like iPad / Android tablets.
    const isPhone =
      typeof navigator !== 'undefined' && /Mobi|iPhone|Android.+Mobile|Windows Phone/i.test(navigator.userAgent || '');
    setIsMobilePhone(isPhone);
  }, []);

  useEffect(() => {
    // Load saved city from localStorage
    const savedCity = localStorage.getItem('selectedCity');
    if (savedCity) {
      setSelectedCity(savedCity);
    }
  }, []);

  useEffect(() => {
    // Filter cities based on search term
    const filtered = cities.filter((city) => city.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredCities(filtered);
  }, [searchTerm]);

  useEffect(() => {
    // Focus search input and scroll to selected city when modal opens
    if (isOpen) {
      // Use requestAnimationFrame for better timing
      const focusInput = () => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          // On mobile, scroll input into view first to ensure it's visible
          if (isMobilePhone) {
            searchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      };

      const scrollToSelectedCity = () => {
        if (selectedCity) {
          const selectedElement = document.querySelector(`.city-item.selected`);
          if (selectedElement) {
            if (isMobilePhone) {
              // On mobile, scroll the selected city to be visible at the top
              const citiesList = document.querySelector('.cities-list');
              if (citiesList && selectedElement) {
                // Wait for layout to settle, then scroll
                setTimeout(() => {
                  // Use getBoundingClientRect to get accurate positions
                  const containerRect = citiesList.getBoundingClientRect();
                  const elementRect = selectedElement.getBoundingClientRect();

                  // Calculate the scroll position needed to bring the selected element to the top
                  // Current scroll position + difference between element and container top
                  const currentScrollTop = citiesList.scrollTop;
                  const elementTopRelativeToContainer = elementRect.top - containerRect.top;

                  // Scroll to position the element at the top of the container
                  citiesList.scrollTop = currentScrollTop + elementTopRelativeToContainer;
                }, 200);
              }
            } else {
              // On larger screens, keep centered behavior
              selectedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }
        }
      };

      // On mobile, focus input first, then scroll to city
      // On desktop, do both simultaneously
      if (isMobilePhone) {
        requestAnimationFrame(() => {
          focusInput();
          setTimeout(() => {
            scrollToSelectedCity();
          }, 200);
        });
      } else {
        requestAnimationFrame(() => {
          focusInput();
          scrollToSelectedCity();
          // Fallback with timeout
          setTimeout(() => {
            focusInput();
            scrollToSelectedCity();
          }, 150);
        });
      }
    }
  }, [isOpen, selectedCity, isMobilePhone]);

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    localStorage.setItem('selectedCity', city);
    onCitySelect(city);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className={`city-modal ${isMobilePhone ? 'city-modal-mobile' : ''}`}
      overlayClassName={`city-modal-overlay ${isMobilePhone ? 'city-modal-overlay-mobile' : ''}`}
      ariaHideApp={false}
    >
      <div className="city-modal-content">
        {!isMobilePhone && (
          <div className="city-modal-header">
            <h2>Izberi mesto</h2>
            <button className="close-button" onClick={handleClose}>
              ×
            </button>
          </div>
        )}

        <div className="city-modal-body">
          <div className="search-container">
            {isMobilePhone && (
              <button className="close-button-mobile" onClick={handleClose}>
                ×
              </button>
            )}
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Iskanje..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="cities-list">
            {filteredCities.map((city, index) => (
              <div
                key={index}
                className={`city-item ${selectedCity === city ? 'selected' : ''}`}
                onClick={() => handleCitySelect(city)}
              >
                {city}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CitySelectionModal;
